import { apiClient } from "@/api/api-client";
import createBaseService from "@/api/base-service";
import type {
	SalesContract,
	SalesContractCalendarEvent,
	SalesContractDashboard,
	SalesContractFilters,
	SalesContractFormData,
} from "@/features/dashboard/types/sales-contract";

class SalesContractService {
	private baseService = createBaseService<SalesContract>("/sales-contracts");

	async list(filters?: SalesContractFilters) {
		return this.baseService.list(filters);
	}

	async findById(id: string) {
		return this.baseService.findById(id);
	}

	async searchTags(search: string): Promise<{ value: string; label: string }[]> {
		const response = await apiClient.get<{ value: string; label: string }[]>("/sales-contracts/tags", {
			params: { search },
		});
		return response.data;
	}

	async create(data: SalesContractFormData) {
		return this.baseService.create(data);
	}

	async update(id: string, data: Partial<SalesContractFormData>) {
		return this.baseService.updateById(id, data);
	}

	async markAsComplete(id: string) {
		const response = await apiClient.post<{ data: string }>(`/sales-contracts/${id}/mark-as-completed`);
		return response.data.data;
	}

	async delete(id: string) {
		return this.baseService.deleteById(id);
	}

	async extend(id: string, extendedEndDate: string): Promise<SalesContract> {
		const response = await apiClient.post<{ data: SalesContract }>(`/sales-contracts/${id}/extend`, { extended_end_date: extendedEndDate });
		return response.data.data;
	}

	async getDashboard(dateFrom?: string, dateTo?: string, status?: string, salespersonId?: number): Promise<SalesContractDashboard> {
		const params: Record<string, string> = {};
		if (dateFrom) params.date_from = dateFrom;
		if (dateTo) params.date_to = dateTo;
		if (status) params.status = status;
		if (salespersonId) params.salesperson_id = String(salespersonId);

		const response = await apiClient.get<SalesContractDashboard>("/sales-contracts-dashboard", { params });
		return response.data;
	}

	async getCalendarEvents(start?: string, end?: string): Promise<SalesContractCalendarEvent[]> {
		const response = await apiClient.get<SalesContractCalendarEvent[]>("/sales-contracts-calendar", { params: { start, end } });
		return response.data;
	}

	async grouped(params: Record<string, unknown> = {}): Promise<{
		grouped_by: string;
		groups: Array<{
			group: string;
			count: number;
			contracts: SalesContract[];
		}>;
	}> {
		const searchParams = new URLSearchParams();
		for (const [key, value] of Object.entries(params)) {
			if (value !== undefined && value !== null) {
				searchParams.append(key, String(value));
			}
		}

		const response = await apiClient.get(`/sales-contracts/grouped?${searchParams}`);
		return response.data;
	}

	async updateCollectionStatus(
		id: string,
		data: {
			collection_status: "pending" | "collected";
			collection_remarks?: string;
			date_collected?: string;
			collected_by?: string;
		},
	): Promise<SalesContract> {
		const response = await apiClient.patch<{ data: SalesContract }>(`/sales-contracts/${id}/collection-status`, data);
		return response.data.data;
	}

	async getCollectionReport(
		year: number,
		salespersonId?: number,
	): Promise<
		Array<{
			id: string;
			client_name: string;
			project_name: string;
			salesperson_name: string;
			contract_amount: number;
			monthly_recurring_sales: number;
			monthly_status: {
				[key: string]: "collected" | "pending" | "not_applicable";
			};
		}>
	> {
		const params = new URLSearchParams({ year: year.toString() });
		if (salespersonId) {
			params.append("salesperson_id", salespersonId.toString());
		}
		const response = await apiClient.get(`/sales-contracts-collection-report?${params}`);
		return response.data;
	}
}

export const salesContractService = new SalesContractService();
